/**
 * The learner's assigned library, fetched once and shared.
 *
 * `visible(token)` is the question every surface asks. It answers TRUE while
 * loading: a catalogue that flickers from empty to full looks broken, and the
 * cost of showing a card half a second early is far lower than the cost of
 * showing a signed-in learner an empty library that then fills in.
 */

import { useEffect, useState } from 'react';
import { fetchMyAccess, type LearnerAccess } from '../../services/learnerAPI';

export function useLearnerAccess() {
  const [access, setAccess] = useState<LearnerAccess | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMyAccess()
      .then((a) => { if (!cancelled) setAccess(a); })
      // Access is a filter, never a gate: if it cannot be resolved the learner
      // keeps whatever the page already had rather than losing the library.
      .catch(() => { if (!cancelled) setAccess({ mode: 'ALL', courseIds: [], lessonTokens: [], visibleTokens: null, visibleCourseIds: null }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tokens = access?.visibleTokens ?? null;
  return {
    access,
    loading,
    restricted: access?.mode === 'ASSIGNED',
    visible: (shareToken: string) => tokens === null || tokens.includes(shareToken),
    courseVisible: (courseId: string | null | undefined) => {
      const ids = access?.visibleCourseIds ?? null;
      return ids === null || (!!courseId && ids.includes(courseId));
    },
    count: tokens?.length ?? null,
  };
}
